/**
 * @file BackupsPage.tsx
 * @description «Платформа → Бэкапы» (phase08.9 post-smoke; phase19.26 —
 * бэкапы прода: база + хранилище + проверка восстановления).
 *
 * Под `PLATFORM_MANAGE_BACKUPS` (маршрут платформы). Расписание исполняет
 * таймер на сервере приложения, настройки хранит API (скрипт читает их в начале
 * запуска), история — отчёты скрипта по webhook.
 *
 * phase19.29: ширина как у остальных страниц (без режима «на всю ширину»),
 * заголовок и «Запустить сейчас» — в верхней панели (`CabinetPageHeader`).
 *
 * Страница = шапка + сводка с dead-man (`BackupSummary`) + настройки
 * (`BackupConfigForm`) + «Где хранить копии» (`BackupDestinationSection`) +
 * ключ доступа агента (`BackupWebhookSection`) + установка (`BackupInstallSection`) +
 * история (`BackupRunsList`). После мутаций данные перечитываются, а не правятся
 * руками. Экстренный отзыв ключа (шаг 7.1, Р2) — общий для двух блоков
 * (`useAgentRevoke`): счётчик `revokeNonce` гасит ранее показанную команду
 * установки, `agentRevoked` — плашка «Ключ отозван» в блоке установки, пока
 * агент снова не отчитается.
 */

import { Loader2, Play, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { BackupConfigForm } from "../../components/platform/backups/BackupConfigForm";
import { BackupDestinationSection } from "../../components/platform/backups/BackupDestinationSection";
import { BackupInstallSection } from "../../components/platform/backups/BackupInstallSection";
import { BackupRunsList } from "../../components/platform/backups/BackupRunsList";
import { BackupSummary } from "../../components/platform/backups/BackupSummary";
import { BackupWebhookSection } from "../../components/platform/backups/BackupWebhookSection";
import { agentActivityMark } from "../../components/platform/backups/backup-agent-activity";
import { useAgentRevoke } from "../../components/platform/backups/use-agent-revoke";
import { Button } from "../../components/ui/button";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import { NoticeStrip } from "../../components/ui/NoticeStrip";
import { useNotify } from "../../components/ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  getBackupConfig,
  listBackupRuns,
  triggerManualBackup,
  type BackupConfigDto,
  type BackupRunDto,
} from "../../lib/api-backups";

const RUNS_LIMIT = 20;

export function BackupsPage() {
  const notify = useNotify();
  const [config, setConfig] = useState<BackupConfigDto | null>(null);
  const [runs, setRuns] = useState<BackupRunDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  /** Перечитать конфиг и историю; вернуть время последнего отчёта агента (undefined — сбой). */
  const refresh = useCallback(async (): Promise<number | null | undefined> => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, history] = await Promise.all([
        getBackupConfig(),
        listBackupRuns({ limit: RUNS_LIMIT }),
      ]);
      setConfig(cfg);
      setRuns(history.items);
      return agentActivityMark(cfg, history.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить настройки бэкапа");
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  const { revokeNonce, agentRevoked, onRevoked } = useAgentRevoke(
    config ? agentActivityMark(config, runs) : null,
    refresh,
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onTrigger() {
    if (!window.confirm("Запустить бэкап вручную сейчас?")) return;
    setTriggering(true);
    try {
      await triggerManualBackup();
      notify.success("Бэкап поставлен в очередь — агент на сервере приложения подхватит его в течение минуты");
      // RUNNING-запись появится сверху истории.
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось запустить бэкап");
    } finally {
      setTriggering(false);
    }
  }

  // Заголовок в панели виден и во время первой загрузки.
  const header = (
    <CabinetPageHeader
      title="Бэкапы"
      subtitle="База данных и файлы копируются на сервере приложения, затем агент проверяет восстановление"
      secondaryActions={
        <Button variant="outline" onClick={() => void refresh()} loading={loading}>
          <RefreshCw className="size-4" />
          Обновить
        </Button>
      }
      action={
        <Button
          variant="brand"
          onClick={() => void onTrigger()}
          loading={triggering}
          disabled={!config?.isEnabled}
        >
          <Play className="size-4" />
          {triggering ? "Запуск…" : "Запустить сейчас"}
        </Button>
      }
    />
  );

  if (loading && !config) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-on-surface-variant" aria-label="Загрузка" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}

      {error ? (
        <NoticeStrip
          tone="error"
          action={
            <Button variant="soft" onClick={() => void refresh()}>
              Повторить
            </Button>
          }
        >
          {error}
        </NoticeStrip>
      ) : null}

      {config ? (
        <>
          <BackupSummary config={config} />
          <BackupConfigForm
            config={config}
            onSaved={(updated) => {
              setConfig(updated);
              void refresh();
            }}
          />
          <BackupDestinationSection
            config={config}
            onChanged={(updated) => {
              // Ответ сохранения — сразу на страницу, в одном кадре с черновиком формы.
              if (updated) setConfig(updated);
              void refresh();
            }}
          />
          <BackupWebhookSection onRevoked={() => void onRevoked()} />
          <BackupInstallSection
            localRoot={config.localRoot}
            agentRevoked={agentRevoked}
            revokeNonce={revokeNonce}
          />
          <BackupRunsList runs={runs} runsLast30Days={config.stats.runsLast30Days} />
        </>
      ) : null}
    </div>
  );
}
